import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import VegIcon from '../assets/Veg.png';
import NonVegIcon from '../assets/Non_veg.png';
import {
  customerOrderService,
  PublicMenuItem,
  PublicOrderItem,
  TableOrderInfo,
} from '../services/customerOrder';
import {
  UtensilsCrossed,
  Plus,
  Minus,
  ShoppingCart,
  Bell,
  X,
  CheckCircle,
  Loader2,
  Search,
  ChefHat,
  ChevronLeft,
  ChevronRight,
  Tag,
  Star,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

interface CartItem extends PublicOrderItem {
  itemName: string;
  specialNotes?: string;
}

export const TableOrders: React.FC = () => {
  const { restaurantId, tableId } = useParams<{ restaurantId: string; tableId: string }>();

  const [tableInfo, setTableInfo] = useState<TableOrderInfo | null>(null);
  const [menu, setMenu] = useState<PublicMenuItem[]>([]);
  const [publicCategories, setPublicCategories] = useState<{ id: string; name: string }[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [waiterCalling, setWaiterCalling] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVegType, setSelectedVegType] = useState<'' | 'VEG' | 'NON_VEG'>('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Per-card image index (for scrolling through multiple images)
  const [activeImageIdx, setActiveImageIdx] = useState<Record<string, number>>({});
  const getImgIdx = (id: string) => activeImageIdx[id] ?? 0;
  const shiftImg = (e: React.MouseEvent, id: string, len: number, dir: 1 | -1) => {
    e.stopPropagation();
    setActiveImageIdx(prev => ({ ...prev, [id]: ((prev[id] ?? 0) + dir + len) % len }));
  };

  // Item detail modal
  const [detailItem, setDetailItem] = useState<PublicMenuItem | null>(null);
  const [detailNotes, setDetailNotes] = useState('');
  const [detailImgIdx, setDetailImgIdx] = useState(0);

  useEffect(() => {
    if (!restaurantId || !tableId) return;
    const load = async () => {
      try {
        const [info, items, cats] = await Promise.all([
          customerOrderService.getTableInfo(restaurantId, tableId),
          customerOrderService.getMenu(restaurantId),
          customerOrderService.getPublicCategories(restaurantId),
        ]);
        setTableInfo(info);
        setMenu(items);
        if (cats.length > 0) setPublicCategories(cats);
      } catch {
        setError('Could not load table information. Please scan the QR code again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [restaurantId, tableId]);

  // Build a category name lookup: item's own categoryName > publicCategories fallback
  const categoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    // First seed from fetched public categories
    publicCategories.forEach(c => map.set(c.id, c.name));
    // Then override with any names that came directly on menu items
    menu.forEach(item => {
      if (item.categoryId && item.categoryName) map.set(item.categoryId, item.categoryName);
    });
    return map;
  }, [menu, publicCategories]);

  // Unique categories from menu (for filter chips)
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const result: { id: string; name: string }[] = [];
    menu.forEach(item => {
      if (item.categoryId && !seen.has(item.categoryId)) {
        seen.add(item.categoryId);
        result.push({ id: item.categoryId, name: categoryNameMap.get(item.categoryId) || item.categoryName || 'Menu' });
      }
    });
    return result;
  }, [menu, categoryNameMap]);

  // Filtered items
  const filteredMenu = useMemo(() => {
    return menu.filter(item => {
      if (!item.available) return false;
      if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (selectedVegType === 'VEG' && item.vegType === 'NON_VEG') return false;
      if (selectedVegType === 'NON_VEG' && item.vegType !== 'NON_VEG') return false;
      if (selectedCategory && item.categoryId !== selectedCategory) return false;
      return true;
    });
  }, [menu, searchTerm, selectedVegType, selectedCategory]);

  // Group by category
  const groupedMenu = useMemo(() => {
    const groups = new Map<string, { label: string; items: PublicMenuItem[] }>();
    filteredMenu.forEach(item => {
      const key = item.categoryId || '__uncategorized__';
      const label = categoryNameMap.get(item.categoryId || '') || item.categoryName || 'Menu';
      if (!groups.has(key)) groups.set(key, { label, items: [] });
      groups.get(key)!.items.push(item);
    });
    return Array.from(groups.values());
  }, [filteredMenu, categoryNameMap]);

  const getQty = (itemId: string) => cart.find(c => c.itemId === itemId)?.quantity || 0;

  const addToCart = (item: PublicMenuItem) => {
    setCart(prev => {
      const existing = prev.find(c => c.itemId === item.id);
      if (existing) return prev.map(c => c.itemId === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { itemId: item.id, itemName: item.name, quantity: 1, unitPrice: item.price }];
    });
  };

  const removeFromCart = (itemId: string) => {
    setCart(prev => {
      const existing = prev.find(c => c.itemId === itemId);
      if (!existing) return prev;
      if (existing.quantity <= 1) return prev.filter(c => c.itemId !== itemId);
      return prev.map(c => c.itemId === itemId ? { ...c, quantity: c.quantity - 1 } : c);
    });
  };

  const updateItemNotes = (itemId: string, notes: string) => {
    setCart(prev => prev.map(c => c.itemId === itemId ? { ...c, specialNotes: notes } : c));
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.unitPrice * c.quantity, 0);
  const cartCount = cart.reduce((sum, c) => sum + c.quantity, 0);

  const handlePlaceOrder = async () => {
    if (!customerName.trim()) { toast.error('Please enter your name'); return; }
    if (!/^[6-9]\d{9}$/.test(customerMobile)) { toast.error('Enter a valid 10-digit mobile number'); return; }
    if (cart.length === 0) { toast.error('Your cart is empty'); return; }
    setSubmitting(true);
    try {
      await customerOrderService.placeOrderRequest(restaurantId!, tableId!, {
        restaurantId: restaurantId!,
        tableId: tableId!,
        customerName: customerName.trim(),
        customerMobile: customerMobile.trim(),
        items: cart.map(c => ({
          itemId: c.itemId,
          itemName: c.itemName,
          quantity: c.quantity,
          unitPrice: c.unitPrice,
          specialNotes: c.specialNotes,
        })),
      });
      setOrderSuccess(true);
      setCart([]);
      setShowOrderForm(false);
      setShowCart(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCallWaiter = async () => {
    setWaiterCalling(true);
    try {
      await customerOrderService.callWaiter(restaurantId!, tableId!);
      toast.success('Waiter has been called! They will be with you shortly.');
    } catch {
      toast.error('Could not call waiter. Please try again.');
    } finally {
      setWaiterCalling(false);
    }
  };

  const openDetail = (item: PublicMenuItem) => {
    setDetailItem(item);
    setDetailNotes(cart.find(c => c.itemId === item.id)?.specialNotes || '');
    setDetailImgIdx(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
          <p className="text-gray-700 font-semibold">Loading your menu...</p>
          <p className="text-gray-400 text-sm mt-1">Just a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm bg-white rounded-2xl p-8 shadow-lg">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <UtensilsCrossed className="h-8 w-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Oops!</h2>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const detailImages = detailItem?.images ?? [];

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      <Toaster position="top-center" toastOptions={{ style: { borderRadius: '12px', fontSize: '14px' } }} />

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 pt-6 pb-16 relative overflow-hidden">
        <div className="absolute -top-8 -right-8 w-28 h-28 bg-white/10 rounded-full" />
        <div className="absolute top-4 right-10 w-14 h-14 bg-white/10 rounded-full" />
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gray-50 rounded-t-3xl" />

        <div className="max-w-lg mx-auto relative z-10">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <UtensilsCrossed className="h-3.5 w-3.5 text-orange-200" />
                <span className="text-orange-100 text-xs font-medium uppercase tracking-wider">Scan &amp; Order</span>
              </div>
              <h1 className="text-2xl font-extrabold tracking-tight">{tableInfo?.table?.name || 'Your Table'}</h1>
              <p className="text-orange-100 text-sm mt-0.5">
                {menu.filter(i => i.available).length} items available
              </p>
            </div>
            <button
              onClick={handleCallWaiter}
              disabled={waiterCalling}
              className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm border border-white/30 text-white px-3 py-2 rounded-xl font-medium text-sm disabled:opacity-60 hover:bg-white/30 transition-colors flex-shrink-0 mt-1"
            >
              <Bell className="h-4 w-4" />
              {waiterCalling ? 'Calling...' : 'Call Waiter'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Search & Filter card (sticky, overlapping header) ── */}
      <div className="sticky top-0 z-30 bg-white shadow-md -mt-6 mx-3 rounded-2xl px-3 pt-3 pb-2.5 max-w-lg lg:mx-auto">
        {/* Search */}
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search dishes..."
            className="w-full pl-9 pr-8 py-2.5 bg-gray-50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 border border-gray-100"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="h-3.5 w-3.5 text-gray-400" />
            </button>
          )}
        </div>

        {/* Veg / Non-Veg chips */}
        <div className="flex gap-1.5 mb-2">
          {(['', 'VEG', 'NON_VEG'] as ('' | 'VEG' | 'NON_VEG')[]).map(val => (
            <button
              key={val}
              onClick={() => setSelectedVegType(val)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border flex-shrink-0 transition-all ${
                selectedVegType === val
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              {val === '' && 'All'}
              {val === 'VEG' && <><img src={VegIcon} alt="Veg" className="h-3.5 w-3.5" /> Veg</>}
              {val === 'NON_VEG' && <><img src={NonVegIcon} alt="Non-Veg" className="h-3.5 w-3.5" /> Non-Veg</>}
            </button>
          ))}
        </div>

        {/* Category chips */}
        {categories.length > 0 && (
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-3 py-1 rounded-full text-xs font-semibold border flex-shrink-0 transition-all ${
                selectedCategory === ''
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? '' : cat.id)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border flex-shrink-0 transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-lg mx-auto px-3 pt-4 space-y-5">

        {/* Active orders banner */}
        {tableInfo?.hasOpenOrder && (tableInfo.currentOrders?.length ?? 0) > 0 && (
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
            <h3 className="font-semibold text-blue-800 text-sm mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse inline-block" />
              Active Order{(tableInfo.currentOrders?.length ?? 0) > 1 ? 's' : ''}
            </h3>
            {tableInfo.currentOrders.map((order: any, idx: number) => (
              <div key={order.id || idx} className={idx > 0 ? 'mt-3 pt-3 border-t border-blue-100' : ''}>
                {(tableInfo.currentOrders?.length ?? 0) > 1 && (
                  <p className="text-xs font-bold text-blue-400 mb-1 uppercase tracking-wide">Order {idx + 1}</p>
                )}
                <div className="space-y-0.5">
                  {order.items?.map((item: any, i: number) => (
                    <div key={i} className="flex justify-between text-sm text-blue-700">
                      <span>{item.name} × {item.quantity}</span>
                      <span>₹{(item.unitPrice * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-bold text-blue-800 pt-2 border-t border-blue-200 mt-1.5 text-sm">
                    <span>Order Total</span>
                    <span>₹{order.total?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Order success */}
        {orderSuccess && (
          <div className="bg-green-50 border border-green-100 rounded-2xl p-5 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="font-bold text-green-800 text-lg">Order Request Sent!</h3>
            <p className="text-green-600 text-sm mt-1">A waiter will confirm your order shortly.</p>
            <button
              onClick={() => setOrderSuccess(false)}
              className="mt-3 text-orange-500 font-semibold text-sm underline"
            >
              Add more items
            </button>
          </div>
        )}

        {/* Menu groups */}
        {groupedMenu.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <UtensilsCrossed className="h-8 w-8 text-gray-300" />
            </div>
            <p className="text-gray-600 font-semibold">No items found</p>
            <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          groupedMenu.map(group => (
            <div key={group.label}>
              <div className="flex items-center gap-2 mb-3 px-0.5">
                <h2 className="font-extrabold text-gray-800 text-base">{group.label}</h2>
                <span className="text-[11px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full font-medium">{group.items.length}</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {group.items.map(item => {
                  const qty = getQty(item.id);
                  const imgs = item.images ?? [];
                  const imgLen = imgs.length;
                  const currIdx = getImgIdx(item.id);

                  return (
                    <div
                      key={item.id}
                      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col active:scale-[0.98] transition-transform cursor-pointer"
                      onClick={() => openDetail(item)}
                    >
                      {/* Image */}
                      <div className="relative h-32 bg-gradient-to-br from-orange-50 to-amber-100 flex-shrink-0 overflow-hidden">
                        {imgLen > 0 ? (
                          <img src={imgs[currIdx]} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ChefHat className="h-10 w-10 text-orange-200" />
                          </div>
                        )}
                        {/* Prev/Next arrows for multiple images */}
                        {imgLen > 1 && (
                          <>
                            <button
                              onClick={e => shiftImg(e, item.id, imgLen, -1)}
                              className="absolute left-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-black/40 rounded-full flex items-center justify-center text-white text-xs font-bold z-10"
                            >‹</button>
                            <button
                              onClick={e => shiftImg(e, item.id, imgLen, 1)}
                              className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-black/40 rounded-full flex items-center justify-center text-white text-xs font-bold z-10"
                            >›</button>
                          </>
                        )}
                        {/* Veg badge */}
                        <div className="absolute top-2 left-2">
                          <img
                            src={item.vegType === 'NON_VEG' ? NonVegIcon : VegIcon}
                            alt={item.vegType}
                            className="h-4 w-4 drop-shadow"
                          />
                        </div>
                        {/* Qty badge */}
                        {qty > 0 && (
                          <div className="absolute top-2 right-2 bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow">
                            {qty}
                          </div>
                        )}
                        {/* Dot indicators */}
                        {imgLen > 1 && (
                          <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1 py-1.5"
                            style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.28))' }}>
                            {imgs.map((_, i) => (
                              <div key={i} className="h-1.5 rounded-full transition-all duration-300"
                                style={{ width: i === currIdx ? '12px' : '6px', backgroundColor: i === currIdx ? '#f97316' : 'rgba(255,255,255,0.65)' }} />
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-2.5 flex flex-col flex-1">
                        <h4 className="text-xs font-bold text-gray-900 leading-snug line-clamp-2 mb-1">{item.name}</h4>
                        <p className="text-sm font-extrabold text-orange-500 mb-2">₹{item.price.toFixed(2)}</p>

                        {/* Add button — stop propagation so card click doesn't open detail */}
                        <div className="mt-auto" onClick={e => e.stopPropagation()}>
                          {qty === 0 ? (
                            <button
                              onClick={() => addToCart(item)}
                              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-1.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 transition-colors"
                            >
                              <Plus className="h-3 w-3" /> Add
                            </button>
                          ) : (
                            <div className="flex items-center justify-between border-2 border-orange-400 rounded-xl px-2 py-1">
                              <button onClick={() => removeFromCart(item.id)} className="text-orange-500">
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <span className="font-bold text-gray-900 text-sm">{qty}</span>
                              <button onClick={() => addToCart(item)} className="text-orange-500">
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {/* ── Give Feedback CTA ── */}
        <a href={`/feedback/${restaurantId}`} className="block mt-6 max-w-lg lg:mx-auto">
          <div
            className="rounded-2xl px-4 py-4 flex items-center justify-between active:scale-[0.99] transition-transform text-white"
            style={{
              background: 'linear-gradient(135deg, #f97316, #ea580c)',
              boxShadow: '0 12px 26px -10px rgba(249,115,22,0.7)',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Star className="h-5 w-5 text-white fill-white" />
              </div>
              <div>
                <p className="text-[15px] font-extrabold">Enjoyed your meal?</p>
                <p className="text-xs text-orange-50">Tap to rate your experience</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-white/90 flex-shrink-0" />
          </div>
        </a>
      </div>

      {/* ── Item Detail Modal ─────────────────────────────── */}
      {detailItem && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end" onClick={() => setDetailItem(null)}>
          <div
            className="bg-white w-full rounded-t-3xl max-h-[92vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Image carousel */}
            <div className="relative h-60 bg-gradient-to-br from-orange-50 to-amber-100 flex-shrink-0">
              {detailImages.length > 0 ? (
                <img src={detailImages[detailImgIdx]} alt={detailItem.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ChefHat className="h-20 w-20 text-orange-200" />
                </div>
              )}

              {detailImages.length > 1 && (
                <>
                  <button
                    onClick={() => setDetailImgIdx(i => (i - 1 + detailImages.length) % detailImages.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 rounded-full flex items-center justify-center text-white"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDetailImgIdx(i => (i + 1) % detailImages.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 rounded-full flex items-center justify-center text-white"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  {/* Dots */}
                  <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1 py-2"
                    style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.25))' }}>
                    {detailImages.map((_: string, i: number) => (
                      <div
                        key={i}
                        className="h-1.5 rounded-full transition-all duration-300"
                        style={{
                          width: i === detailImgIdx ? '12px' : '6px',
                          backgroundColor: i === detailImgIdx ? '#f97316' : 'rgba(255,255,255,0.65)',
                        }}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Close */}
              <button
                onClick={() => setDetailItem(null)}
                className="absolute top-3 right-3 w-8 h-8 bg-black/40 rounded-full flex items-center justify-center text-white"
              >
                <X className="h-4 w-4" />
              </button>

              {/* Veg badge */}
              <div className="absolute top-3 left-3">
                <img
                  src={detailItem.vegType === 'NON_VEG' ? NonVegIcon : VegIcon}
                  alt={detailItem.vegType}
                  className="h-6 w-6 drop-shadow"
                />
              </div>
            </div>

            <div className="px-5 py-5 space-y-4 pb-8">
              {/* Name + category */}
              <div>
                {(detailItem.categoryName || (detailItem.categoryId && categoryNameMap.get(detailItem.categoryId))) && (
                  <p className="text-xs text-orange-500 font-semibold flex items-center gap-1 mb-1">
                    <Tag className="h-3 w-3" /> {categoryNameMap.get(detailItem.categoryId || '') || detailItem.categoryName}
                  </p>
                )}
                <h2 className="text-xl font-extrabold text-gray-900">{detailItem.name}</h2>
                {detailItem.description && (
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">{detailItem.description}</p>
                )}
              </div>

              <p className="text-2xl font-extrabold text-orange-500">₹{detailItem.price.toFixed(2)}</p>

              {/* Qty controls */}
              <div className="flex items-center gap-4">
                <span className="text-sm font-semibold text-gray-700">Quantity</span>
                {getQty(detailItem.id) === 0 ? (
                  <button
                    onClick={() => addToCart(detailItem)}
                    className="flex items-center gap-1.5 bg-orange-500 text-white px-5 py-2 rounded-xl text-sm font-bold"
                  >
                    <Plus className="h-4 w-4" /> Add
                  </button>
                ) : (
                  <div className="flex items-center gap-3 border-2 border-orange-400 rounded-xl px-3 py-2">
                    <button onClick={() => removeFromCart(detailItem.id)} className="text-orange-500">
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="font-extrabold text-gray-900 w-6 text-center">{getQty(detailItem.id)}</span>
                    <button onClick={() => addToCart(detailItem)} className="text-orange-500">
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Special notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Special Instructions <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={detailNotes}
                  onChange={e => {
                    setDetailNotes(e.target.value);
                    if (getQty(detailItem.id) > 0) updateItemNotes(detailItem.id, e.target.value);
                  }}
                  placeholder="e.g. Less spicy, no onion..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
              </div>

              {getQty(detailItem.id) === 0 ? (
                <button
                  onClick={() => {
                    addToCart(detailItem);
                    if (detailNotes) updateItemNotes(detailItem.id, detailNotes);
                    setDetailItem(null);
                    toast.success(`${detailItem.name} added to cart!`);
                  }}
                  className="w-full text-white py-4 rounded-2xl font-extrabold text-base flex items-center justify-center gap-2 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #f97316, #f59e0b)' }}
                >
                  <ShoppingCart className="h-5 w-5" />
                  Add to Cart · ₹{detailItem.price.toFixed(2)}
                </button>
              ) : (
                <button
                  onClick={() => setDetailItem(null)}
                  className="w-full bg-gray-100 text-gray-700 py-4 rounded-2xl font-bold text-base"
                >
                  Done ✓
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Floating cart button ──────────────────────────── */}
      {cartCount > 0 && !showCart && !showOrderForm && !detailItem && (
        <div className="fixed bottom-6 left-0 right-0 z-40 flex justify-center px-4">
          <button
            onClick={() => setShowCart(true)}
            className="flex items-center gap-3 text-white px-5 py-3.5 rounded-2xl shadow-2xl font-bold w-full max-w-sm justify-between"
            style={{ background: 'linear-gradient(135deg, #f97316, #f59e0b)' }}
          >
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <ShoppingCart className="h-5 w-5" />
                <span className="absolute -top-2 -right-2 bg-white text-orange-500 text-[10px] font-extrabold rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              </div>
              <span>{cartCount} item{cartCount !== 1 ? 's' : ''} added</span>
            </div>
            <span className="font-extrabold">₹{cartTotal.toFixed(2)} →</span>
          </button>
        </div>
      )}

      {/* ── Cart Sheet ─────────────────────────────────────── */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end" onClick={() => setShowCart(false)}>
          <div className="bg-white w-full rounded-t-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-lg font-extrabold text-gray-900 flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-orange-500" /> Your Order
              </h2>
              <button onClick={() => setShowCart(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
              {cart.map(item => (
                <div key={item.itemId} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="font-semibold text-gray-900 text-sm truncate">{item.itemName}</p>
                      <p className="text-xs text-gray-400">₹{item.unitPrice.toFixed(2)} each</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="flex items-center gap-2 border-2 border-orange-300 rounded-xl px-2 py-1">
                        <button onClick={() => removeFromCart(item.itemId)} className="text-orange-500">
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="font-bold text-gray-900 text-sm w-4 text-center">{item.quantity}</span>
                        <button onClick={() => addToCart({ id: item.itemId, name: item.itemName, price: item.unitPrice, vegType: 'VEG', available: true })} className="text-orange-500">
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <span className="font-bold text-gray-700 text-sm w-14 text-right">₹{(item.unitPrice * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={item.specialNotes || ''}
                    onChange={e => updateItemNotes(item.itemId, e.target.value)}
                    placeholder="Add a note..."
                    className="w-full border border-gray-100 rounded-xl px-3 py-1.5 text-xs text-gray-600 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-orange-300"
                  />
                </div>
              ))}
            </div>

            <div className="px-5 py-4 border-t bg-gray-50 rounded-b-none">
              <div className="flex justify-between mb-4 font-extrabold text-gray-900 text-lg">
                <span>Total</span>
                <span className="text-orange-500">₹{cartTotal.toFixed(2)}</span>
              </div>
              <button
                onClick={() => { setShowCart(false); setShowOrderForm(true); }}
                className="w-full text-white py-3.5 rounded-xl font-extrabold text-base"
                style={{ background: 'linear-gradient(135deg, #f97316, #f59e0b)' }}
              >
                Proceed to Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Customer Details Form ─────────────────────────── */}
      {showOrderForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end" onClick={() => setShowOrderForm(false)}>
          <div className="bg-white w-full rounded-t-3xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-lg font-extrabold text-gray-900">Your Details</h2>
              <button onClick={() => setShowOrderForm(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div className="px-5 py-5 space-y-4 pb-10">
              <p className="text-sm text-gray-500">Provide your details to confirm the order.</p>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Name *</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="Your name"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mobile Number *</label>
                <input
                  type="tel"
                  value={customerMobile}
                  onChange={e => setCustomerMobile(e.target.value)}
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
              </div>

              <div className="bg-orange-50 rounded-2xl p-4 border border-orange-100">
                <p className="text-sm font-bold text-orange-800 mb-2">
                  {cartCount} item{cartCount !== 1 ? 's' : ''} · ₹{cartTotal.toFixed(2)}
                </p>
                {cart.map(item => (
                  <div key={item.itemId} className="flex justify-between text-sm text-orange-700 py-0.5">
                    <span>{item.itemName} × {item.quantity}</span>
                    <span>₹{(item.unitPrice * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={handlePlaceOrder}
                disabled={submitting}
                className="w-full text-white py-4 rounded-2xl font-extrabold text-base disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #f97316, #f59e0b)' }}
              >
                {submitting
                  ? <><Loader2 className="h-5 w-5 animate-spin" /> Placing Order...</>
                  : 'Place Order Request'}
              </button>

              <p className="text-xs text-gray-400 text-center">
                A waiter will confirm your order before processing.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
